// src/layouts/products/components/ProductDetailsView/PricebooksTab.js
import React, { useState } from 'react';

// --- Material-UI Core Components ---
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Tooltip,
  Card,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import CircleIcon from '@mui/icons-material/Circle'; // Import the icon

// --- Argon Design System Components ---
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

function PricebooksTab({ product, renderFieldsProp }) {
  const [expandedPricebook, setExpandedPricebook] = useState({});

  if (!product || !product.related_data?.pricebooks?.length) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Pricebooks available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handlePricebookClick = (pricebookId) => {
    setExpandedPricebook((prevState) => ({
      ...prevState,
      [pricebookId]: !prevState[pricebookId],
    }));
  };

  return (
    <ArgonBox p={3}>
      {product.related_data.pricebooks.map((pricebook) => {
        const pricebookId = pricebook.sfId || pricebook.id;
        const hasPriceEntries = pricebook.priceEntries && pricebook.priceEntries.length > 0;

        return (
          <Card key={pricebookId} sx={{ marginBottom: '1rem' }}>
            <ArgonBox mb={0.5} mt={0.5}>
              {/* --- Clickable Header for each Pricebook --- */}
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handlePricebookClick(pricebookId)}
                style={{ cursor: 'pointer' }}
                p={1}
                borderRadius="md"
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                  {pricebook.fields?.Name || pricebook.Name || pricebook.name}
                </ArgonTypography>
                <IconButton
                  aria-label="expand pricebook"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent parent onClick from firing again
                    handlePricebookClick(pricebookId);
                  }}
                >
                  <Icon>{expandedPricebook[pricebookId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              {/* --- Collapsible Content for the Pricebook --- */}
              <Collapse in={expandedPricebook[pricebookId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2}>
                  {/* Render general pricebook fields */}
                  {renderFieldsProp(pricebook.fields)}

                  {/* --- Table for Price Entries --- */}
                  {hasPriceEntries && (
                    <ArgonBox mt={3}>
                      <TableContainer component={Paper}>
                        <Table size="small" aria-label="price entries">
                          <TableHead sx={{ display: 'contents' }}>
                            <TableRow>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                                  Unit Price
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                                  Currency
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold" mb={1}>
                                  Status
                                </ArgonTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pricebook.priceEntries.map((entry) => (
                              <TableRow key={entry.sfId || entry.id}>
                                <TableCell>
                                  {entry.fields?.UnitPrice != null ? entry.fields.UnitPrice : 'N/A'}
                                </TableCell>
                                <TableCell>{entry.fields?.CurrencyIsoCode || 'EUR'}</TableCell>
                                <TableCell>
                                  <Tooltip title={entry.fields?.IsActive ? 'Active' : 'Inactive'}>
                                    {entry.fields?.IsActive ? (
                                      // Green icon for Active
                                      <CircleIcon color="success" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                                    ) : (
                                      // Grey icon for Inactive
                                      <CircleIcon color="disabled" sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                                    )}
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </ArgonBox>
                  )}
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default PricebooksTab;
